/**
 * Shared image-status utilities.
 *
 * Centralises provider detection that was previously duplicated across
 * LeadDetail.js, Leads.js, and inline in LeadBuyerMatches.js.
 */

/** Classify a URL as a supported cloud-storage provider, or null. */
export function getImageFolderProvider(url) {
    if (!url || typeof url !== 'string') return null;
    const lower = url.trim().toLowerCase();
    if (lower.includes('drive.google.com') || lower.includes('docs.google.com')) return 'drive';
    if (lower.includes('dropbox.com')) return 'dropbox';
    if (lower.includes('onedrive.live.com') || lower.includes('1drv.ms') || lower.includes('sharepoint.com')) return 'oneDrive';
    return null;
}

/** Return true when the URL points to a provider we cannot download from. */
export function isUnsupportedImageProvider(url) {
    if (!url || typeof url !== 'string') return false;
    const lower = url.trim().toLowerCase();
    return lower.includes('photos.google.com') ||
           lower.includes('photos.app.goo.gl') ||
           lower.includes('googleusercontent.com') ||
           lower.includes('lh3.google.com') ||
           lower.includes('lh4.google.com') ||
           lower.includes('lh5.google.com') ||
           lower.includes('lh6.google.com');
}

/**
 * True when a URL is a downloaded custom-folder / uploaded property photo.
 *
 * The ingestion pipeline writes Drive/Dropbox/OneDrive folder downloads (and
 * direct image uploads) to `/underwriting_images/{companyId}/…`. Enrichment
 * street views land in `/property_images/{zpid}/…` instead, so this path is a
 * reliable "the operator gave us these photos" signal.
 */
export function isFolderPhoto(url) {
    return typeof url === 'string' && url.toLowerCase().includes('/underwriting_images/');
}

/**
 * True when a URL is *definitively* map / street-view / satellite imagery.
 *
 * Kept deliberately narrow to avoid demoting real photos:
 *  - `maps.googleapis.com` — raw Google street-view / static-map URLs.
 *  - `/property_images/{zpid}/{streetview|satellite_|roadmap_}…` — the named
 *    map assets in the per-zpid enrichment cache.
 *
 * NOTE: we do NOT treat every `/property_images/` URL as a map asset. That
 * cache also holds real photos (`repaircost_*`, `rc_img_*`) for older leads,
 * and street views uploaded as `rc_img_*` are indistinguishable from real
 * photos by URL — those are handled by promoting folder photos instead.
 */
export function isMapOrStreetViewImage(url) {
    if (!url || typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return lower.includes('maps.googleapis.com') ||
           /\/property_images\/[^/]+\/(streetview|satellite_|roadmap_)/.test(lower);
}

/**
 * The single display gate that DROPS non-renderable URLs — folder links
 * (Drive/Dropbox/etc) and Google Photos share/album links (photos.google.com,
 * photos.app.goo.gl, googleusercontent, lh*) that return HTML, not an image.
 * Preserves the incoming order. Use this (not prioritizePropertyPhotos) when an
 * explicit operator photoOrder is already applied and must win as-is.
 */
export function dropNonRenderableImages(urls) {
    if (!Array.isArray(urls)) return [];
    return urls.filter(url =>
        url && typeof url === 'string' &&
        !isUnsupportedImageProvider(url) && !getImageFolderProvider(url)
    );
}

/**
 * Stable 3-way partition so a lead's custom-folder photos take absolute
 * priority: folder/uploaded photos first, then everything else in place, then
 * definitive map/street-view imagery last. Relative order within each group is
 * preserved. Run this as the LAST step.
 *
 * Pass `{ respectOrder: true }` when an explicit operator photoOrder has
 * already been applied: that skips the folder-first partition (which would
 * otherwise yank uploaded photos ahead of an MLS photo the operator chose as
 * featured) and only drops non-renderable URLs, preserving the given order.
 *
 * Pass `{ featuredUrl }` to lead with a specific photo (the backend-chosen
 * front-exterior shot — see functions pickFrontFacadeUrl). When present in the
 * list it is promoted to index 0 so the gallery opens on the house's front like
 * Zillow does, regardless of upload order. Ignored under respectOrder: an
 * explicit operator photoOrder always wins over the automatic featured pick.
 *
 * Also acts as the single display gate that DROPS non-renderable URLs, so
 * centralizing the drop here keeps every gallery/featured-image surface
 * consistent regardless of how its source arrays were pre-filtered.
 */
/**
 * Upgrade a bare rdcpix (Realtor CDN) thumbnail URL to a full-size one.
 * Mirrors functions/utils/imageOrdering.js upgradeRdcpixUrl — kept in sync by
 * hand, like prioritizePropertyPhotos above.
 *
 * Realtor's gallery URLs arrive as `…-{m|b|f}###s.jpg`, which is 120x80. The
 * size is chosen by appending `-w{W}_h{H}` before `.jpg`. Two reasons this
 * matters on the display side:
 *
 *  1. Leads ingested before the backend fix stored the raw thumbnails, so their
 *     galleries render as postage stamps. Upgrading at read time fixes them
 *     without re-running anything.
 *  2. After a re-run, repairCostImageUrls holds the UPGRADED form while
 *     imageUrls may still hold the RAW one. deriveDisplayImageUrls merges both
 *     and dedupes by string, so without a shared normal form every Realtor photo
 *     would appear twice — once full-size, once as a thumbnail.
 *
 * Host-scoped + idempotent: only bare rdcpix thumbnails are rewritten.
 */
const RDCPIX_BARE_THUMB = /^(https?:\/\/[^/?#]*\.rdcpix\.com\/[^?#]*-[mbf]\d+s)\.jpg$/i;
export function upgradeRdcpixUrl(url) {
    if (typeof url !== 'string') return url;
    const m = url.match(RDCPIX_BARE_THUMB);
    return m ? `${m[1]}-w1600_h1200.jpg` : url;
}

export function prioritizePropertyPhotos(urls, { respectOrder = false, featuredUrl = null } = {}) {
    if (!Array.isArray(urls)) return [];
    if (respectOrder) return dropNonRenderableImages(urls);
    const folder = [];
    const middle = [];
    const maps = [];
    for (const url of urls) {
        if (!url || typeof url !== 'string') continue;
        if (isUnsupportedImageProvider(url) || getImageFolderProvider(url)) continue;
        if (isMapOrStreetViewImage(url)) maps.push(url);
        else if (isFolderPhoto(url)) folder.push(url);
        else middle.push(url);
    }
    const ordered = [...folder, ...middle, ...maps];
    if (featuredUrl && typeof featuredUrl === 'string') {
        const idx = ordered.indexOf(featuredUrl);
        if (idx > 0) {
            ordered.splice(idx, 1);
            ordered.unshift(featuredUrl);
        }
    }
    return ordered;
}

/**
 * Canonical display-image derivation shared by LeadDetailV2 + LeadBuyerMatches
 * so both surfaces stay byte-for-byte identical. Returns the ordered, render-
 * safe photo URLs for a lead.
 *
 * Order of precedence:
 *   1. classifier-labelled URLs (detail surfaces pass imageLabels; cards don't)
 *   2. fallback merge: repairCostImageUrls → imageUrls → enrichment thumbs
 * Excluded (operator-deleted) URLs are filtered out. When the operator has a
 * saved photoOrder, that order wins as-is (respectOrder) so a chosen featured
 * photo actually leads; otherwise the backend-chosen front-exterior photo
 * (lead.featuredImageUrl) leads, falling back to folder-first priority.
 */
// A label set this small, on a lead with this many photos available, is a fossil
// of the rdcpix-thumbnail bug rather than a real description of the gallery —
// see the note at the override below. Absolute thresholds on purpose.
const MAX_DEGENERATE_LABELS = 4;
const MIN_PHOTOS_TO_OVERRIDE_LABELS = 20;

/**
 * The label set a display surface should actually use — `imageLabels`, or null
 * when it's a degenerate fossil of the rdcpix-thumbnail bug (see the note in
 * deriveDisplayImageUrls).
 *
 * Callers must run this ONCE and use the result for both the URL derivation and
 * anything else fed the labels — notably ScrollableImages, which groups by room
 * whenever it receives a non-empty label set. Hand it 2 stale labels next to a
 * 50-photo fallback and every photo lands in a single "Other" section instead of
 * the clean flat grid, because none of them match a label.
 */
export function resolveDisplayLabels(lead, imageLabels) {
    if (!lead || !imageLabels?.labels?.length) return imageLabels ?? null;
    const excluded = new Set((lead.excludedImageUrls || []).map(upgradeRdcpixUrl));
    const labelled = imageLabels.labels
        .map((l) => l?.firebaseUrl)
        .filter((u) => u && typeof u === 'string' && !excluded.has(u));
    const fallbackCount = [
        ...(lead.repairCostImageUrls || []),
        ...(lead.imageUrls || []),
    ].filter((u) => u && typeof u === 'string').length;
    const degenerate =
        labelled.length > 0 &&
        labelled.length <= MAX_DEGENERATE_LABELS &&
        fallbackCount >= MIN_PHOTOS_TO_OVERRIDE_LABELS;
    return degenerate ? null : imageLabels;
}

export function deriveDisplayImageUrls(lead, imageLabels) {
    if (!lead) return [];
    // Compare exclusions in the same normal form as the display URLs, otherwise
    // an exclusion recorded against a raw thumbnail stops matching once the URL
    // is upgraded (and the operator's removed photo reappears).
    const excluded = new Set((lead.excludedImageUrls || []).map(upgradeRdcpixUrl));
    let displaySet;

    const labelled = [];
    if (imageLabels?.labels?.length) {
        const seen = new Set();
        for (const label of imageLabels.labels) {
            const url = label?.firebaseUrl;
            if (!url || typeof url !== 'string' || seen.has(url) || excluded.has(url)) continue;
            seen.add(url);
            labelled.push(url);
        }
    }

    const fallback = (() => {
        const repairImages = lead.repairCostImageUrls || [];
        const leadImages = (lead.imageUrls || []).filter(url =>
            url && !isUnsupportedImageProvider(url) && !getImageFolderProvider(url)
        );
        const photos = lead.photoUrls || {};
        const enrichmentThumbs = [photos.zillow, photos.redfin, photos.realtor]
            .filter(url => url && typeof url === 'string' && url.trim().length > 0);
        const seen = new Set();
        // Upgrade BEFORE deduping: the two arrays can hold the same Realtor photo
        // in different sizes, and only a shared normal form collapses them.
        return [...repairImages, ...leadImages, ...enrichmentThumbs]
            .map(upgradeRdcpixUrl)
            .filter(url => {
                if (!url || seen.has(url) || excluded.has(url)) return false;
                seen.add(url);
                return true;
            });
    })();

    // Labels normally WIN, because ScrollableImages groups the gallery by room and
    // grouping only works when a displayed URL matches a label's firebaseUrl.
    //
    // But that assumed the label set describes the whole gallery. On a lead hit by
    // the rdcpix-thumbnail bug it doesn't: the classifier only ever saw the 2-3
    // images that survived the 300px gate, so the labels are a fossil of the bug —
    // and preferring them hid a 50-photo gallery behind two thumbnails. Craig's
    // four leads each had 2 labels against ~75 stored URLs.
    //
    // The override is deliberately an ABSOLUTE small-N test, not a ratio: a healthy
    // lead's fallback array legitimately holds BOTH the storage and original form
    // of every photo, so its length can be ~2x the label count and a ratio would
    // throw away good labels. A classifier that genuinely ran over 20+ photos
    // always emits more than a handful of labels, so this can only fire on a
    // degenerate set. Discard rather than append: labels hold storage URLs and the
    // fallback holds originals, so appending would show every photo twice.
    const labelsAreDegenerate =
        labelled.length > 0 &&
        labelled.length <= MAX_DEGENERATE_LABELS &&
        fallback.length >= MIN_PHOTOS_TO_OVERRIDE_LABELS;

    displaySet = (labelled.length > 0 && !labelsAreDegenerate) ? labelled : fallback;

    // photoOrder and featuredImageUrl are matched against displaySet, so they need
    // the same normal form — an operator ordering saved against raw thumbnails
    // must keep working after those URLs are upgraded.
    if (Array.isArray(lead.photoOrder) && lead.photoOrder.length > 0) {
        const inDisplay = new Set(displaySet);
        const orderedSeen = new Set();
        const ordered = [];
        for (const raw of lead.photoOrder) {
            const url = upgradeRdcpixUrl(raw);
            if (inDisplay.has(url) && !orderedSeen.has(url)) {
                orderedSeen.add(url);
                ordered.push(url);
            }
        }
        const remaining = displaySet.filter(url => !orderedSeen.has(url));
        return prioritizePropertyPhotos([...ordered, ...remaining], { respectOrder: true });
    }
    return prioritizePropertyPhotos(displaySet, { featuredUrl: upgradeRdcpixUrl(lead.featuredImageUrl) });
}

/**
 * Map an in-flight imageDownloadStatus to a human progress label.
 *
 * The backend (runDownloadLeadImages) writes a `stage` field while
 * state==='processing': 'downloading' → 'analyzing'. Returns null once the
 * download is no longer in progress so callers can drop the indicator.
 * Label has no trailing ellipsis — add one at the call site if desired.
 *
 * @param {object} imageDownloadStatus – lead.imageDownloadStatus
 * @returns {string|null}
 */
export function getImageDownloadStageLabel(imageDownloadStatus) {
    if (!imageDownloadStatus || imageDownloadStatus.state !== 'processing') return null;
    if (imageDownloadStatus.stage === 'analyzing') return 'Analyzing repair estimate';
    return 'Downloading photos';
}

/** Bound on how long a lead's photos may be considered "still coming". */
const PHOTO_COLLECTION_WINDOW_MS = 30 * 60 * 1000;

/**
 * Milliseconds from any timestamp shape this app actually hands around.
 *
 * ⚠️ THE `{_seconds}` CASE IS THE LOAD-BEARING ONE. A lead read straight from Firestore
 * (LeadDetailV2, LeadBuyerMatches) carries real Timestamps with `.toDate()`, but a lead
 * that came through the getOrderedDocs wire — which is every row in the leads TABLE —
 * carries the serialized `{_seconds, _nanoseconds}` form. `new Date({_seconds})` is an
 * Invalid Date, every comparison against NaN is false, and both windows below then
 * silently report "not in flight" for the entire table. Returns null rather than NaN so
 * callers have to decide what an unreadable timestamp means instead of inheriting a
 * comparison that is quietly always false.
 */
function timestampMs(value) {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    if (typeof value === 'object' && value._seconds !== undefined) {
        return value._seconds * 1000 + (value._nanoseconds || 0) / 1000000;
    }
    const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/**
 * True while an imageDownloadStatus-driven download is genuinely in flight.
 * Bounded so a lead left in 'processing' by a Cloud Run kill (the 540s trigger
 * timeout — see functions/downloadLeadImages.js) doesn't spin forever.
 *
 * @param {object} imageDownloadStatus – lead.imageDownloadStatus
 * @returns {boolean}
 */
export function isImageDownloadInProgress(imageDownloadStatus) {
    const startedAt = timestampMs(imageDownloadStatus?.startedAt);
    return imageDownloadStatus?.state === 'processing'
        && startedAt != null
        && (Date.now() - startedAt) < PHOTO_COLLECTION_WINDOW_MS;
}

/**
 * True when a lead's submitted photos haven't landed yet, so the ONLY images we
 * could render are Street View / map stubs.
 *
 * Why this exists: `photoUrls.{zillow,redfin,realtor}` are Google Street View
 * stubs, and deriveDisplayImageUrls falls back to them when no real photos are
 * attached. Callers that only ask "do I have a hero image?" therefore render a
 * confident 2-photo gallery of Street View while a 69-photo Dropbox folder is
 * mid-download — which reads to the operator as "only 2 photos got pulled"
 * (customer report, 2026-07-16; the folder had in fact landed ~5 min later).
 *
 * Detecting the window is the subtle part: phase-1 collection
 * (functions/propertyIngestionV2/underwriting/imagePipeline.js) does the
 * download inline with enrichment and never writes `imageDownloadStatus`, so
 * isImageDownloadInProgress cannot see it. `photoAvailability` IS written once
 * collection settles — by BOTH writers (imagePipeline and runDownloadLeadImages)
 * — so its absence, inside a bounded window from lead creation, is the signal
 * that photos are still coming. Once it exists, "no real photos" is a real
 * answer (folder empty/failed) and callers should show their folder affordance.
 *
 * @param {object} lead – lead data (imageFolders, imageUrls, imageDownloadStatus,
 *                        photoAvailability, createdAt)
 * @param {string[]} displayImageUrls – output of deriveDisplayImageUrls for this lead
 * @returns {boolean}
 */
export function arePhotosPending(lead, displayImageUrls) {
    if (!lead) return false;
    // Any genuinely non-map image means the photos are already here.
    if ((displayImageUrls || []).some(url => !isMapOrStreetViewImage(url))) return false;

    const folders = lead.imageFolders || {};
    const hasSubmittedAssets =
        ['drive', 'dropbox', 'oneDrive', 'zips'].some(b => (folders[b] || []).length > 0)
        || (lead.imageUrls || []).length > 0;
    if (!hasSubmittedAssets) return false;

    if (isImageDownloadInProgress(lead.imageDownloadStatus)) return true;

    // A terminal download outcome is settled: "no real photos" is the answer,
    // not a wait. Checked BEFORE the photoAvailability/window fallback because
    // callers order their failure branch after this one — without it a folder
    // that failed inside the window would show a spinner instead of its
    // re-paste-to-retry affordance, and would keep doing so until the window
    // aged out. Also means we don't depend on the best-effort (fail-open)
    // photoAvailability write in imagePipeline.js having landed.
    const dlState = lead.imageDownloadStatus?.state;
    if (dlState === 'failed' || dlState === 'complete') return false;

    // Collection has settled — zero real photos is the truth, not a wait.
    if (lead.photoAvailability) return false;

    const createdAt = timestampMs(lead.createdAt);
    return createdAt != null
        && (Date.now() - createdAt) < PHOTO_COLLECTION_WINDOW_MS;
}

// The bar for "this lead has a gallery, not a stray link". Shared by the
// direct-URL branch and the displayed-photo branch so a single pasted image URL
// is treated the same either way.
const MIN_PHOTOS_FOR_SUPPORTED = 2;

/**
 * Derive whether a lead has adequate property images.
 *
 * Supported when any of the following holds:
 *  - the lead has a Drive / Dropbox / OneDrive folder link, OR
 *  - imageUrls contains a Drive / Dropbox / OneDrive URL, OR
 *  - imageUrls contains ≥2 usable direct URLs (excluding Google Photos hosts
 *    that the backend cannot fetch from), OR
 *  - the caller is already displaying ≥2 real property photos.
 *
 * That last clause exists because `imageUrls` is only ONE of the places photos
 * live, and on an on-market lead it is usually the empty one: ingestion writes
 * MLS photos to `repairCostImageUrls` and the room classifier writes its copies
 * to the property doc's `subjectImageRoomLabels`. deriveDisplayImageUrls reads
 * both; this function read neither. So a lead rendering a full MLS gallery still
 * reported 'none', and the page told the operator to paste a folder link for
 * photos it was showing them at the time.
 *
 * Street-view / map stubs do NOT count — deriveDisplayImageUrls falls back to
 * them precisely when a lead has no real photos, so a lead showing only those
 * genuinely has none and the folder affordance is right.
 *
 * @param {object} lead – lead data (expects imageFolders, imageUrls)
 * @param {string} [_leadType] – kept for API compatibility, no longer used
 * @param {string[]} [displayImageUrls] – output of deriveDisplayImageUrls for this
 *        lead. Omit it and the check falls back to the imageUrls-only behaviour.
 * @returns {'supported'|'unsupported'|'none'}
 */
export function getImageStatus(lead, _leadType, displayImageUrls) {
    if (!lead) return 'none';

    const folders = lead.imageFolders || {};
    if (folders.drive?.[0] || folders.dropbox?.[0] || folders.oneDrive?.[0]) return 'supported';

    // Real photos already on screen answer the question outright, whichever
    // field they were sourced from.
    const realDisplayed = (displayImageUrls || []).filter(u => u && !isMapOrStreetViewImage(u));
    if (realDisplayed.length >= MIN_PHOTOS_FOR_SUPPORTED) return 'supported';

    const urls = lead.imageUrls || [];
    for (const u of urls) {
        if (getImageFolderProvider(u)) return 'supported';
    }

    const directUrls = urls.filter(u => u && !getImageFolderProvider(u) && !isUnsupportedImageProvider(u));
    if (directUrls.length >= MIN_PHOTOS_FOR_SUPPORTED) return 'supported';
    if (directUrls.length > 0) return 'unsupported';

    // No usable URLs but had something (e.g. only Google Photos links) → unsupported, else none
    return urls.some(u => u && typeof u === 'string') ? 'unsupported' : 'none';
}

// Our images are served raw + full-resolution from Cloud Storage (e.g.
// 1536x2048 ≈ 12 MB *decoded* each — mobile Safari decodes by pixel count and
// kills the tab past its budget). Route cover/card images through the
// /v1/img resize endpoint so the browser decodes a ~640px variant (~2 MB)
// instead. Full-res is reserved for the fullscreen gallery, where only a few
// images are visible at once. Pass-through for anything that isn't one of OUR
// Storage objects (enrichment thumbs, already-proxied URLs) so callers can wrap
// every card image unconditionally.
//
// Re-enabled after #866: the endpoint now FAILS OPEN on every error branch
// (302 → original) and the deal feed pre-warms each variant at mirror time, so
// the failure modes that broke cards before (a non-ok source fetch collapsed to
// a "403 JSON", and a '..'-in-filename 400) can no longer render a broken image.
const RESIZE_API_ORIGIN = 'https://api.clozers.co';
const RESIZABLE_HOST = 'storage.googleapis.com';
const RESIZABLE_BUCKET = 'clozers.firebasestorage.app';

// rdcpix sizes itself: `…-{m|b|f}###s` takes an optional `-w{W}_h{H}` suffix and
// the CDN returns the image fitted to that box. We ask for the display size
// directly rather than routing through our resize endpoint (no proxy hop, no
// cost). This matters because deriveDisplayImageUrls normalizes rdcpix to the
// 1600px form for the fullscreen gallery — feeding THAT to a feed of cards would
// be ~6.8 MB decoded each, exactly the mobile-Safari budget problem this whole
// helper exists to avoid.
const RDCPIX_SIZED = /^(https?:\/\/[^/?#]*\.rdcpix\.com\/[^?#]*-[mbf]\d+s)(?:-w\d+_h\d+)?\.jpg$/i;

/** Rewrite an own-bucket Storage URL to its resized variant; pass others through. */
export function resizedImageUrl(url, width = 640) {
    if (!url || typeof url !== 'string') return url;

    const rdc = url.match(RDCPIX_SIZED);
    if (rdc) return `${rdc[1]}-w${width}_h${Math.round(width * 0.75)}.jpg`;

    let parsed;
    try { parsed = new URL(url); } catch (e) { return url; }
    // Only rewrite our own Storage objects; never proxy third-party hosts.
    if (parsed.hostname !== RESIZABLE_HOST) return url;
    if (!parsed.pathname.replace(/^\/+/, '').startsWith(`${RESIZABLE_BUCKET}/`)) return url;
    return `${RESIZE_API_ORIGIN}/v1/img?u=${encodeURIComponent(url)}&w=${width}`;
}
